import { useEffect, useState, useCallback, useMemo } from "react";
import ReactSelect from "react-select";
import {
    useAddSchemeToSessionsMutation,
    useGetSchemesHallQuery,
    useGetTypeSeatMutation
} from "../../../features/CallApi";
import { useForm } from "react-hook-form";
import { Button, Form } from "react-bootstrap";

// Константы для конфигурации
const PAGE_SIZE = 10;
const DEBOUNCE_DELAY = 300;

// Вынесено за пределы компонента для предотвращения пересоздания при каждом рендере
const mapSchemeToOption = (scheme) => ({
    value: scheme.id,
    label: `# ${scheme.id} ${scheme.name}`
});

export const SchemeSelect = ({ showId, refetch, selSchedule }) => {
    const [pageIndex, setPageIndex] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [selectedScheme, setSelectedScheme] = useState(null);

    const { handleSubmit, setValue, formState: { errors } } = useForm({
        defaultValues: {
            showId,
            schemeId: null
        }
    });

    const { data, isLoading: isLoadingSchemes } = useGetSchemesHallQuery({
        pageIndex,
        pageSize: PAGE_SIZE,
        stringFilter: debouncedSearchTerm
    });

    const [addSchemeToSessions, { isLoading: isAddingScheme }] = useAddSchemeToSessionsMutation();
    const [getTypeSeat, { isLoading: isUpdatingSeats }] = useGetTypeSeatMutation();

    // Debounce для поиска
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Обновление формы при изменении showId
    useEffect(() => {
        setValue("showId", showId);
    }, [showId, setValue]);

    const schemeOptions = useMemo(() => {
        return data?.getSchemeHalls?.map(mapSchemeToOption) || [];
    }, [data]);

    const handleSchemeChange = useCallback((option) => {
        if (option) {
            setSelectedScheme(option);
            setValue("schemeId", option.value);
        } else {
            setSelectedScheme(null);
            setValue("schemeId", null);
        }
    }, [setValue]);

    const handleSearchInput = useCallback((inputValue) => {
        setSearchTerm(inputValue);
        setPageIndex(1); // Сброс страницы при поиске
    }, []);

    const handleAddScheme = useCallback(async (formData) => {
        if (!formData.schemeId) {
            window.alert("Выберите схему");
            return;
        }

        try {
            await addSchemeToSessions(formData).unwrap();
            refetch();
        } catch (error) {
            console.error("Ошибка при добавлении схемы:", error);
            window.alert("Произошла ошибка при добавлении схемы");
        }
    }, [addSchemeToSessions, refetch]);

    const handleUpdateSeats = useCallback(async () => {
        if (!selectedScheme?.value || !selSchedule) {
            return;
        }

        const isConfirmed = window.confirm(
            "Все места сессии будут вновь добавлены на схему, продолжить?"
        );

        if (!isConfirmed) return;

        try {
            await getTypeSeat({
                schemeId: selectedScheme.value,
                showId: selSchedule
            }).unwrap();
            refetch();
        } catch (error) {
            console.error("Ошибка при обновлении мест:", error);
            window.alert("Произошла ошибка при обновлении мест");
        }
    }, [selectedScheme, selSchedule, getTypeSeat, refetch]);

    const isLoading = isLoadingSchemes || isAddingScheme || isUpdatingSeats;

    return (
        <div className="scheme-select-container">
            <Form onSubmit={handleSubmit(handleAddScheme)}>
                <ReactSelect
                    name="schemeselect"
                    className="basic-single mb-1"
                    placeholder="Схема зала..."
                    classNamePrefix="select"
                    options={schemeOptions}
                    value={selectedScheme}
                    onInputChange={handleSearchInput}
                    onChange={handleSchemeChange}
                    isLoading={isLoadingSchemes}
                    isDisabled={isLoading}
                    isClearable
                    required
                    noOptionsMessage={() => "Схемы не найдены"}
                    loadingMessage={() => "Загрузка..."}
                />

                <Button
                    className="mb-1"
                    size="sm"
                    variant="success"
                    type="submit"
                    disabled={isLoading || !selectedScheme}
                >
                    {isAddingScheme ? "Сохранение..." : "Связать схему"}
                </Button>
            </Form>

            <Button
                size="sm"
                className="mt-2"
                variant="success"
                disabled={!selectedScheme || !selSchedule || isLoading}
                onClick={handleUpdateSeats}
            >
                {isUpdatingSeats ? "Обновление..." : "Связать места на схеме"}
            </Button>

            {/* Отображение ошибок формы */}
            {errors.schemeId && (
                <Form.Text className="text-danger">
                    {errors.schemeId.message}
                </Form.Text>
            )}
        </div>
    );
};